import type { FirebaseOptions } from "firebase/app";
import {
    getAuth,
    isSignInWithEmailLink,
    signInWithEmailLink,
    onAuthStateChanged as firebaseOnAuthStateChanged,
    signOut,
    type Auth,
    type User as FirebaseUser
} from "firebase/auth";
import type { IAM as Interface } from "../../design/services";
import type { User, Authorization, Config } from "../../design/types";
import { getFirebaseApp } from "../shared/firebase";

export class IAM implements Interface {

    authz: Authorization[] = [];
    currentUser: User | null = null;

    private auth: Auth;
    private onLoginCallback: (user: User) => void;
    private onLogoutCallback: () => void;

    // Private + readonly so callers can only supply the callback through the
    // constructor — post-construction reassignment would reintroduce the race
    // where firebaseOnAuthStateChanged fires before the handler is wired.
    private readonly onFirebaseUser: ((firebaseUser: FirebaseUser | null) => void) | null;

    constructor(config: Config, onLogin: (user: User) => void, onLogout: () => void, onFirebaseUser?: (firebaseUser: FirebaseUser | null) => void) {
        const options: FirebaseOptions = {
            apiKey: config.apiKey,
            authDomain: config.authDomain,
            projectId: config.projectId,
            storageBucket: config.storageBucket,
            messagingSenderId: config.messagingSenderId,
            appId: config.appId,
            measurementId: config.measurementId,
        };
        this.auth = getAuth(getFirebaseApp(options));
        this.onLoginCallback = onLogin;
        this.onLogoutCallback = onLogout;
        // Wire before firebaseOnAuthStateChanged so a cached session's first fire
        // is handled by the caller instead of falling through to the default path.
        this.onFirebaseUser = onFirebaseUser ?? null;

        // Complete email-link sign-in if returning from magic link
        if (isSignInWithEmailLink(this.auth, window.location.href)) {
            const email = window.localStorage.getItem('emailForSignIn');
            if (email) {
                signInWithEmailLink(this.auth, email, window.location.href)
                    .then(() => {
                        window.localStorage.removeItem('emailForSignIn');
                        window.history.replaceState(null, '', window.location.pathname);
                    })
                    .catch(e => console.error('[iam] email link sign-in failed:', e));
            }
        }

        firebaseOnAuthStateChanged(this.auth, (firebaseUser) => {
            if (this.onFirebaseUser) {
                this.onFirebaseUser(firebaseUser);
            } else if (!firebaseUser) {
                this.currentUser = null;
                this.onLogoutCallback();
            }
        });
    }

    ["constructor"](config: Config, onLogin: (user: User) => void, onLogout: () => void, onFirebaseUser?: (firebaseUser: FirebaseUser | null) => void): Interface {
        return new IAM(config, onLogin, onLogout, onFirebaseUser);
    }

    login(user: User): void {
        this.currentUser = user;
        this.onLoginCallback(user);
    }

    async logout(): Promise<void> {
        await signOut(this.auth);
        this.currentUser = null;
        this.onLogoutCallback();
    }

    /** Expose Firebase Auth instance for FirebaseUI components */
    getAuth(): Auth {
        return this.auth;
    }
}
